import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const dest =
        u.role === "parent"
          ? "/dashboard/parent"
          : u.role === "recruiter"
          ? "/dashboard/recruiter"
          : u.role === "athlete"
          ? "/dashboard/athlete"
          : u.role === "super_admin" || u.role === "tester_admin"
          ? "/dashboard/admin"
          : "/";
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative flex-col justify-between p-12 overflow-hidden bg-[#0a0a0a] border-r border-white/5">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(0,229,255,0.18), transparent 60%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-md bg-electric flex items-center justify-center">
              <Shield size={20} className="text-black" />
            </div>
            <div className="font-display text-2xl font-black tracking-[0.18em]">VERIFIED</div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-display text-5xl xl:text-6xl font-black uppercase tracking-[0.01em] leading-[0.82]">
            The truth<br />
            <span className="text-electric">runs first.</span>
          </h2>
          <p className="text-white/50 mt-6 max-w-md">
            Verified measurables. Verified testing. Independent scouting opinions trusted by parents,
            athletes and recruiters across the country.
          </p>
        </div>
        <div className="relative text-xs text-white/30">© OmniSportsGroup</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="label-mono mb-3">Sign In</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight">
            Welcome back.
          </h1>
          <p className="text-white/50 text-sm mt-3">Access your VERIFIED dashboard.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="label-mono block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark w-full rounded-md px-4 py-3 text-sm"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label-mono block mb-2">Password</label>
              <PasswordInput
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark w-full rounded-md px-4 py-3 text-sm"
                placeholder="••••••••"
                autoComplete="current-password"
                testId="login-password-input"
              />
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-white/50 hover:text-electric"
                  data-testid="login-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center disabled:opacity-50"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-6 text-sm text-white/50 text-center">
            New here?{" "}
            <Link to="/register" className="text-electric hover:underline">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
